import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react'
import { FacturaAPI } from '../api/facturas.api'
import { sileo } from 'sileo'

const BulkScheduledContext = createContext()

export const useBulkScheduled = () => {
  const context = useContext(BulkScheduledContext)
  if (!context) {
    throw new Error('useBulkScheduled must be used within a BulkScheduledProvider')
  }
  return context
}

export const BulkScheduledProvider = ({ children }) => {
  const facturaApi = useMemo(() => new FacturaAPI(), [])

  const [isProcessing, setIsProcessing] = useState(false)
  const [documents, setDocuments] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [results, setResults] = useState([])
  const [isFinished, setIsFinished] = useState(false)
  const [currentBusiness, setCurrentBusiness] = useState(null)
  const [masterEntityData, setMasterEntityData] = useState(null)

  const processingRef = useRef(false)
  const resultsRef = useRef([])

  const getRowAmount = row => {
    return (row.details || []).reduce(
      (acc, item) => acc + Number(item.unit_price || 0) * Number(item.quantity || 0),
      0
    )
  }

  const emitSingleDocument = async (row, index, business, entityData) => {
    try {
      const payload = {
        dte_type: String(row.tipo_documento),
        sender: Number(entityData.id),
        receiver_tax_id: row.rut_receptor,
        frequency: row.frecuencia,
        details: (row.details || []).map(d => ({
          item_name: d.item_name,
          item_description: d.item_description,
          quantity: Number(d.quantity),
          unit_price: Number(d.unit_price),
          unit_of_measurement: 'UN',
          item_code: '',
          item_type_code: ''
        })),
        day_of_month: ['monthly', 'quarterly', 'biannual', 'yearly'].includes(row.frecuencia) ? Number(row.dia_del_mes) : undefined,
        day_of_week: row.frecuencia === 'weekly' ? Number(row.dia_de_semana) : undefined,
        start_date: row.fecha_inicio,
        end_date: row.fecha_fin || undefined,
        end_type: row.tipo_finalizacion || undefined,
        max_occurrences: row.max_emisiones ? Number(row.max_emisiones) : undefined,
        currency: row.currency || 'CLP',
        currency_day: row.currency === 'UF' ? Number(row.dia_uf || 1) : undefined,
        status: 'active',
        references: []
      }
      debugger
      await facturaApi.crearDocumentosProgramados(business.id, entityData.id, payload)
      debugger
      const rowAmount = getRowAmount(row)
      return { index, doc: row, status: 'success', message: 'Programación creada exitosamente', amount: rowAmount }
    } catch (error) {
      console.error('Error creando programado:', error)
      return {
        index,
        doc: row,
        status: 'error',
        message: error?.response?.data?.message || error?.message || 'Error desconocido'
      }
    }
  }

  const startEmission = useCallback(async (docsToProcess, business, entityData) => {
    if (processingRef.current) return

    setIsProcessing(true)
    setDocuments(docsToProcess)
    setResults([])
    setIsFinished(false)
    setCurrentBusiness(business)
    setMasterEntityData(entityData)
    setCurrentIndex(0)

    processingRef.current = true
    resultsRef.current = []

    const processLoop = async () => {
      for (let i = 0; i < docsToProcess.length; i++) {
        setCurrentIndex(i)
        const docResult = await emitSingleDocument(docsToProcess[i], i, business, entityData)
        resultsRef.current = [...resultsRef.current, docResult]
        setResults([...resultsRef.current])
      }

      const exitosos = resultsRef.current.filter(r => r.status === 'success').length
      const fallidos = resultsRef.current.filter(r => r.status === 'error').length

      setCurrentIndex(docsToProcess.length)
      setIsFinished(true)
      setIsProcessing(false)
      processingRef.current = false

      if (fallidos > 0) {
        sileo.warning({
          title: 'Programaciones Finalizadas',
          description: `Se procesaron ${docsToProcess.length} filas. ${exitosos} exitosas y ${fallidos} con errores.`
        })
      } else {
        sileo.success({
          title: '¡Programaciones Completadas!',
          description: `Se han configurado exitosamente ${docsToProcess.length} documentos recurrentes.`
        })
      }
    }

    processLoop()
  }, [facturaApi])

  const resetEmission = useCallback(() => {
    if (processingRef.current) return
    setIsProcessing(false)
    setDocuments([])
    setCurrentIndex(0)
    setResults([])
    setIsFinished(false)
    setCurrentBusiness(null)
    setMasterEntityData(null)
  }, [])

  const value = {
    isProcessing,
    documents,
    currentIndex,
    results,
    isFinished,
    currentBusiness,
    masterEntityData,
    startEmission,
    resetEmission
  }

  return (
    <BulkScheduledContext.Provider value={value}>
      {children}
    </BulkScheduledContext.Provider>
  )
}
